import React, { forwardRef, useContext, CSSProperties, SVGAttributes } from 'react';
import classnames from 'classnames';
import VizContext from './VizContext';

export interface Props extends SVGAttributes<SVGElement> {
  /**
   * Adjusts the bottom position of the artboard clip mask, a positive number increases it outwards from the bottom.
   */
  offsetArtboardClipMaskBottom?: number;
  /**
   * Adjusts the left position of the artboard clip mask, a positive number increases it outwards from the left.
   */
  offsetArtboardClipMaskLeft?: number;
  /**
   * Adjusts the right position of the artboard clip mask, a positive number increases it outwards from the right.
   */
  offsetArtboardClipMaskRight?: number;
  /**
   * Adjusts the top position of the artboard clip mask, a positive number increases it outwards from the top.
   */
  offsetArtboardClipMaskTop?: number;
  /**
   * Adjusts the rounding of the Artboard clipmask
   */
  rxArtboardClipMask?: number;
  /**
   * Adjusts the rounding of the Artboard clipmask
   */
  ryArtboardClipMask?: number;
}

const styleContent: CSSProperties = {
  position: 'absolute',
  top: '0',
  left: '0',
  height: '100%',
  width: '100%',
};

/**
 * The VizSVG component adds a SVG layer for the visualisation. Any number
 * of these can be used, but only SVG components should be used inside.
 */
const VizSVG = forwardRef<SVGSVGElement, Props>((props: Props, ref) => {
  const {
    children,
    className,
    offsetArtboardClipMaskBottom = 0,
    offsetArtboardClipMaskLeft = 0,
    offsetArtboardClipMaskRight = 0,
    offsetArtboardClipMaskTop = 0,
    rxArtboardClipMask = 0,
    ryArtboardClipMask = 0,
    style,
    ...rest
  } = props;

  const {
    artboardLeft,
    artboardTop,
    artboardWidth,
    artboardHeight,
    defArtboardClipMaskId,
    height,
    width,
  } = useContext(VizContext);

  const artboardClipMaskLeft = artboardLeft - offsetArtboardClipMaskLeft;
  const artboardClipMaskTop = artboardTop - offsetArtboardClipMaskTop;
  const artboardClipMaskHeight = artboardHeight + offsetArtboardClipMaskTop + offsetArtboardClipMaskBottom;
  const artboardClipMaskWidth = artboardWidth + offsetArtboardClipMaskLeft + offsetArtboardClipMaskRight;

  return (
    <svg { ...rest }
        className={ classnames('Bitviz__content Bitviz__content-svg', className) }
        ref={ ref }
        style={ { ...styleContent, ...style } }
        viewBox={ `0 0 ${width || 0} ${height}` }>
      <defs>
        <clipPath id={ defArtboardClipMaskId }>
          <rect
              height={ artboardClipMaskHeight }
              rx={ rxArtboardClipMask }
              ry={ ryArtboardClipMask }
              width={ artboardClipMaskWidth }
              x={ artboardClipMaskLeft }
              y={ artboardClipMaskTop } />
        </clipPath>
      </defs>

      { children }
    </svg>
  );
});

export default VizSVG;
